import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import AppIcon from '../components/AppIcon';
import { friendlyError } from '../utils/friendlyError';
import TruckLoader from '../components/TruckLoader';
import './SolShell.css';

// ── Helpers ────────────────────────────────────────────────────────
function formatTimeAgo(date) {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

function actionColor(type) {
  switch (type) {
    case 'CREATE':   return { bg: '#e8f5e9', fg: '#2e7d32' };
    case 'UPDATE':   return { bg: '#e3f2fd', fg: '#1565c0' };
    case 'DELETE':   return { bg: '#ffebee', fg: '#c62828' };
    case 'LOGIN':    return { bg: '#f3e5f5', fg: '#6a1b9a' };
    case 'VIEW':     return { bg: '#fff8e1', fg: '#f57f17' };
    case 'DOWNLOAD': return { bg: '#e0f2f1', fg: '#00695c' };
    case 'UPLOAD':   return { bg: '#ffe0b2', fg: '#e65100' };
    default:         return { bg: '#f5f5f5', fg: '#616161' };
  }
}

function actionIcon(type) {
  switch (type) {
    case 'CREATE':   return 'plus';
    case 'UPDATE':   return 'pencil';
    case 'DELETE':   return 'trash';
    case 'LOGIN':    return 'login';
    case 'VIEW':     return 'view';
    case 'DOWNLOAD': return 'note';
    case 'UPLOAD':   return 'upload';
    default:         return 'note';
  }
}

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1048576).toFixed(1)} MB`;
}

const ActivityLog = () => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin, canUpload, getIdToken, profileError, profileLoading } = useAuth();

  const [activities, setActivities] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

  const displayName = useMemo(
    () => user?.displayName || user?.email || 'User',
    [user]
  );

  const avatar = useMemo(() => {
    const basis = (user?.displayName || user?.email || 'U').trim();
    const parts = basis.split(/\s+/).filter(Boolean);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : basis[0].toUpperCase();
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  // Redirect non-admins (unless we want to show profile error)
  useEffect(() => {
    if (profileLoading) return; // wait until profile is known before redirecting
    if (!isAdmin && !profileError) navigate('/kpi-dashboard', { replace: true });
  }, [isAdmin, profileError, profileLoading, navigate]);

  // Fetch data
  useEffect(() => {
    if (!isAdmin || !user) return;
    const CACHE_KEY = 'sol_activity_cache';
    const CACHE_TTL = 60_000; // 60 seconds
    const load = async () => {
      // Serve from cache for instant navigation
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          const { ts, stats, acts } = JSON.parse(raw);
          if (Date.now() - ts < CACHE_TTL) {
            setUserStats(stats);
            setActivities(acts);
            setLoading(false);
            return;
          }
        }
      } catch { /* ignore */ }
      setLoading(true);
      setError('');
      try {
        const token = await getIdToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, actRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/users/stats`, { headers }),
          fetch(`${apiBaseUrl}/api/users/activity?limit=100`, { headers }),
        ]);
        const stats = statsRes.ok ? await statsRes.json() : null;
        if (stats) setUserStats(stats);
        if (actRes.ok) {
          const data = await actRes.json();
          const acts = data?.activity || [];
          setActivities(acts);
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), stats, acts })); } catch { /* ignore */ }
        } else {
          throw new Error('Failed to load activity');
        }
      } catch (e) {
        setError(friendlyError(e, 'Failed to load activity log.'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isAdmin]);

  // Filter
  const filteredActivities = useMemo(() => {
    if (filterType === 'ALL') return activities;
    return activities.filter((a) => a.activity_type === filterType);
  }, [activities, filterType]);

  // Count by type
  const typeCounts = useMemo(() => {
    const counts = {};
    activities.forEach((a) => {
      counts[a.activity_type] = (counts[a.activity_type] || 0) + 1;
    });
    return counts;
  }, [activities]);

  if (!isAdmin && profileError) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Access Denied</h2>
        <p>You don't have access, please request access.</p>
        <p style={{ color: '#b00020' }}>Profile error: {profileError}</p>
        {user?.email && (
          <p style={{ fontSize: '0.9em', color: '#666' }}>Signed in as: {user.email}</p>
        )}
      </div>
    );
  }

  return (
    <div className="sol-shell">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      {/* ── Sidebar (same as Dashboard) ── */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="logo">
          <h1>
            <div className="logo-icon"><AppIcon name="truck" /></div>
            <span>SOL Transport</span>
          </h1>
        </div>

        <div className="nav-section">
          <div className="nav-section-title">Main Menu</div>
          <Link className="nav-item" to="/kpi-dashboard">
            <span className="nav-item-icon"><AppIcon name="dashboard" /></span>
            Dashboard
          </Link>

        </div>

        <div className="nav-section">
          <div className="nav-section-title">Administration</div>
          {isAdmin && <Link className="nav-item" to="/user-management">
            <span className="nav-item-icon"><AppIcon name="users" /></span>
            User Management
          </Link>}
          {isAdmin && <Link className="nav-item" to="/source-config">
            <span className="nav-item-icon"><AppIcon name="link" /></span>
            Source Config
          </Link>}
          {isAdmin && <Link className="nav-item active" to="/activity-log">
            <span className="nav-item-icon"><AppIcon name="activity" /></span>
            User Activity Log
          </Link>}
          {canUpload && <Link className="nav-item" to="/upload">
            <span className="nav-item-icon"><AppIcon name="upload" /></span>
            Upload Files
          </Link>}
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{avatar}</div>
          <div className="user-info">
            <div className="user-name">{displayName}</div>
            <div className="user-role">Admin</div>
          </div>
          <button className="logout-btn" type="button" onClick={handleLogout} aria-label="Logout">
            <AppIcon name="login" size={14} />
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main className="main-content">
        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn" 
          type="button" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <AppIcon name="menu" size={18} />
        </button>

        <header className="header">
          <div className="header-left">
            <h2 className="title-with-icon"><AppIcon name="activity" /> User Activity Log</h2>
            <p>Track all user actions across the system</p>
          </div>
          <div className="header-right">
            <span style={{ fontSize: '0.9em', color: '#666' }}>
              {activities.length} total actions
            </span>
          </div>
        </header>

        {/* ── User stat cards ── */}
        {userStats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon blue"><AppIcon name="users" /></div>
              </div>
              <div className="stat-value">{userStats.total_users ?? 0}</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon green"><AppIcon name="check" /></div>
              </div>
              <div className="stat-value">{userStats.active_users ?? 0}</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon purple"><AppIcon name="shield" /></div>
              </div>
              <div className="stat-value">{userStats.admin_users ?? 0}</div>
              <div className="stat-label">Admin Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon orange"><AppIcon name="star" /></div>
              </div>
              <div className="stat-value">{userStats.super_admin_users ?? 0}</div>
              <div className="stat-label">Super Admins</div>
            </div>
          </div>
        )}

        {/* ── Filter bar ── */}
        <div className="filters-bar" style={{ marginTop: 16 }}>
          <div className="filter-group">
            <span className="filter-label">Filter:</span>
          </div>
          {['ALL', 'LOGIN', 'CREATE', 'UPDATE', 'DELETE', 'VIEW', 'DOWNLOAD', 'UPLOAD'].map((t) => (
            <button
              key={t}
              type="button"
              className={`source-tab ${filterType === t ? 'active' : ''}`}
              onClick={() => setFilterType(t)}
              style={{ fontSize: '0.85em' }}
            >
              {t === 'ALL' ? `All (${activities.length})` : `${t} (${typeCounts[t] || 0})`}
            </button>
          ))}
        </div>

        {/* ── Error / Loading ── */}
        {error && (
          <div style={{ padding: 12, backgroundColor: '#ffebee', color: '#b00020', borderRadius: 8, margin: '12px 0' }}>
            {error}
          </div>
        )}

        {/* ── Activity table ── */}
        <div className="table-card" style={{ marginTop: 16 }}>
          <div className="table-header">
            <div className="table-title">User Activity Timeline</div>
          </div>

          {loading ? (
            <div style={{ padding: '32px 0', display: 'flex', justifyContent: 'center' }}>
              <TruckLoader message="Loading activity…" size="sm" />
            </div>
          ) : filteredActivities.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              {filterType === 'ALL'
                ? 'No activity recorded yet. Create, update, or delete users to see entries here.'
                : `No ${filterType} actions found.`}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>When</th>
                  <th style={{ width: 180 }}>Who</th>
                  <th style={{ width: 100 }}>Action</th>
                  <th>Description</th>
                  <th style={{ width: 180 }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivities.map((a) => {
                  const ts = new Date(a.timestamp);
                  const timeAgo = formatTimeAgo(ts);
                  const colors = actionColor(a.activity_type);
                  const icon = actionIcon(a.activity_type);

                  return (
                    <tr key={a.id}>
                      {/* When */}
                      <td
                        style={{ whiteSpace: 'nowrap', fontSize: '0.85em', color: '#555' }}
                        title={ts.toLocaleString()}
                      >
                        {timeAgo}
                      </td>

                      {/* Who */}
                      <td>
                        <div style={{ fontSize: '0.9em', fontWeight: 500 }}>
                          {a.actor_name || '—'}
                        </div>
                        <div style={{ fontSize: '0.78em', color: '#888' }}>
                          {a.actor_email}
                        </div>
                      </td>

                      {/* Action badge */}
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '3px 10px',
                            borderRadius: 12,
                            fontSize: '0.8em',
                            fontWeight: 600,
                            backgroundColor: colors.bg,
                            color: colors.fg,
                          }}
                        >
                          <AppIcon name={icon} /> {a.activity_type}
                        </span>
                      </td>

                      {/* Description */}
                      <td style={{ fontSize: '0.9em' }}>
                        {a.description || `${a.activity_type} ${a.entity_type || ''}`}
                      </td>

                      {/* Detail — metadata for UPLOAD/DOWNLOAD */}
                      <td style={{ fontSize: '0.82em', color: '#888' }}>
                        {a.activity_type === 'UPLOAD' && a.metadata ? (
                          <span>
                            {a.metadata.file_type && <span style={{ marginRight: 6, textTransform: 'uppercase', fontWeight: 600 }}>{a.metadata.file_type}</span>}
                            {a.metadata.size ? fmtBytes(a.metadata.size) : ''}
                            {a.metadata.gcs_path && <span style={{ display: 'block', color: '#64748b', fontSize: '0.9em', wordBreak: 'break-all' }}>{a.metadata.gcs_path}</span>}
                          </span>
                        ) : a.activity_type === 'DOWNLOAD' && a.metadata ? (
                          <span>
                            {a.metadata.format && <span style={{ fontWeight: 600 }}>{String(a.metadata.format).toUpperCase()}</span>}
                            {a.metadata.sections && Array.isArray(a.metadata.sections) && (
                              <span style={{ marginLeft: 6, color: '#64748b' }}>{a.metadata.sections.join(', ')}</span>
                            )}
                          </span>
                        ) : a.entity_type ? (
                          <span style={{ color: '#64748b' }}>{a.entity_type}{a.entity_id ? ` #${a.entity_id}` : ''}</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default ActivityLog;
